import { useEffect, useState } from "react";
import { getOpenState, type OpenState } from "@/lib/hours";

type Props = {
  hours: string | null | undefined;
  /** Overrides the badge with a closed state (e.g. temporarily closed). */
  forceClosedLabel?: string;
  /** Use light text for the closed state when sitting on a dark surface. */
  onDark?: boolean;
  className?: string;
};

/**
 * Live "Open now" / "Closed" badge, worked out in Gatlinburg (Eastern) time.
 * Renders nothing until mounted so server and browser markup always match.
 */
const OpenNowBadge = ({ hours, forceClosedLabel, onDark = false, className = "" }: Props) => {
  const [state, setState] = useState<OpenState | null>(null);

  useEffect(() => {
    const update = () => setState(getOpenState(hours));
    update();
    const id = window.setInterval(update, 60_000);
    return () => window.clearInterval(id);
  }, [hours]);

  if (forceClosedLabel) {
    return (
      <span
        className={`inline-flex items-center gap-2 bg-destructive px-3 py-1 text-sm font-bold uppercase tracking-widest text-destructive-foreground font-body ${className}`}
      >
        {forceClosedLabel}
      </span>
    );
  }

  if (!state) return null;

  return (
    <span
      className={`inline-flex items-center gap-2 text-sm font-body ${
        state.isOpen
          ? "bg-accent px-3 py-1 font-bold uppercase tracking-widest text-accent-foreground"
          : onDark
            ? "font-semibold text-secondary-foreground/80"
            : "font-semibold text-foreground/60"
      } ${className}`}
    >

      {state.isOpen && (
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full bg-accent-foreground"
        />
      )}
      {state.label}
    </span>
  );
};

export default OpenNowBadge;
